// src/pages/DBCLandingPage.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import CheckIcon from "../../../components/common/CheckIcon";
import modulesData from "../../../data/modules.json";
import founder from "../../../assets/images/Blurred shirt.png";

const DBCLandingPage: React.FC = () => {
  const [spotsRemaining] = useState<number>(18);

  // Import modules from JSON
  const modules = modulesData.modules;

  // const testimonials = [
  //   {
  //     name: "Chiamaka O.",
  //     role: "HR Manager, Lagos",
  //     text: "Within 3 weeks after Module 1 I had my first 20 paying customers. I made ₦180,000 in my first month while still working my 9–5.",
  //   },
  //   {
  //     name: "Emeka I.",
  //     role: "IT Consultant, Abuja",
  //     text: "SweetSpot Formula gave me clarity. Within 3 months I had an automated funnel earning ₦350,000/month.",
  //   },
  //   {
  //     name: "Fatima M.",
  //     role: "Banking Professional, Port Harcourt",
  //     text: "I batch content for 2 hours each weekend. WhatsApp list grew to 400 in 6 weeks — ₦420k last month.",
  //   },
  // ];

  // Quick arithmetic proof example (rendered as text)
  const proofBullets = [
    {
      title: "Small, consistent income adds up",
      body: "₦50,000 extra per week = ₦200,000/month. Over 12 months that's ₦2.4M — roughly 2–3x a typical mid-level salary increase.",
    },
    {
      title: "One paid client can replace hours of overtime",
      body: "If you charge ₦15,000 per client for a simple service and get 4 clients/month → ₦60,000/month. A single repeat client doubles that impact.",
    },
    {
      title: "Products scale — one delivery, many buyers",
      body: "Create a simple digital product (workbook, mini-course) once. Sell to 100 people at ₦5,000 = ₦500,000 without trading more hours.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-brandNavy">
      {/* HERO - single column, centered, brandNavy */}
      <header className="bg-brandNavy text-white py-20 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            Your Skills Are Worth More Than Your Salary. <br />
            Here's the Proof.
          </h1>

          <p className="text-2xl md:text-2xl text-white/90 mb-6">
            You were trained to chase promotions. That gave stability — not
            freedom. But the same skills that keep your job can create
            repeatable income streams that out-earn your paycheck. This page
            proves how — with clear math, real examples and a step-by-step plan.
          </p>

          <p className="text-base md:text-2xl text-white/80 mb-6 font-semibold">
            Read the proof. See the path. Decide with confidence.
          </p>

          <a
            href="#problem"
            className="inline-block bg-brandRed text-white px-8 py-3 rounded-2xl font-bold text-2xl hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl"
          >
            Show Me the Proof →
          </a>
        </div>
      </header>

      {/* Full-width image (height restricted to 30vh) */}
      <div className="w-full overflow-hidden">
        <img
          src={founder}
          alt="Professional at work — DBC"
          className="w-full object-cover"
          style={{ height: "30vh", maxHeight: "30vh" }}
        />
      </div>

      {/* P = PROBLEM */}
      <section id="problem" className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">The Problem</h2>
          <p className="text-2xl text-mutedGray mb-6">
            You work hard, you build competence, and you get paid a salary —
            usually tied to a job that could disappear overnight. Meanwhile,
            people with similar skills monetize them online and create multiple
            income streams. The core difference isn't talent — it's strategy.
          </p>

          <div className="bg-white rounded-2xl p-6 shadow-md text-left">
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="text-brandRed text-2xl">▸</div>
                <div>
                  <strong>Single income risk:</strong> one payroll, one company,
                  one source.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="text-brandRed text-2xl">▸</div>
                <div>
                  <strong>Time-for-money trap:</strong> your income grows only
                  if you trade more hours or climb the corporate ladder.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="text-brandRed text-2xl">▸</div>
                <div>
                  <strong>Market confusion:</strong> you don't know which skill
                  will actually sell and to whom.
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* A = AMPLIFY */}
      <section className="py-12 px-6 bg-red-50">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Why that problem costs you real money
          </h3>
          <p className="text-2xl text-mutedGray mb-6">
            Not starting isn't neutral — it's expensive. Below is a
            conservative, realistic example showing how modest side income
            compounds into substantial wealth over time.
          </p>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-brandNavy">₦50k</div>
                <div className="text-sm text-mutedGray">per week</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-brandNavy">₦200k</div>
                <div className="text-sm text-mutedGray">per month</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-brandRed">₦2.4M</div>
                <div className="text-sm text-mutedGray">per year</div>
              </div>
            </div>

            <p className="mt-6 text-mutedGray">
              If a small, consistent side income of ₦50,000/week sounds doable —
              that becomes ₦2.4M in a year. Compare that to typical raises or
              bonuses: side income compounds faster and is in your control.
            </p>
          </div>
        </div>
      </section>

      {/* S = STORY / SOLUTION (Introduce course mid-page) */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-6">
            A simple story — and the solution it revealed
          </h3>

          <p className="text-2xl text-mutedGray mb-6 text-center">
            I was a professional with a steady salary. I taught people, coded,
            managed teams — but my income never matched the value I delivered.
            Instead of waiting for permission, I systematically turned one
            regular skill into a repeatable product and one reliable outreach
            channel. Within months, my side income matched a significant portion
            of my salary.
          </p>

          <div className="bg-brandLightNavy/5 border-2 border-brandNavy/10 rounded-2xl p-6">
            <h4 className="font-bold text-brandNavy mb-3">What changed</h4>
            <ul className="space-y-2 text-mutedGray">
              <li>
                Stop guessing: discover which of your skills will actually sell.
              </li>
              <li>
                Build authority: grow an audience that trusts you (email &
                WhatsApp).
              </li>
              <li>
                Convert simply: use small, automated funnels that sell while you
                sleep.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* T = TRANSFORMATION / TESTIMONY / PROOF */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-6">
            Real proof — not hype
          </h3>

          <p className="text-2xl text-mutedGray text-center mb-8">
            These are conservative, real-world examples and student outcomes —
            numbers are rounded to show how skills turn into cash.
          </p>

          {/* Proof bullets (math + examples) */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {proofBullets.map((p, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 shadow-md text-left"
              >
                <h4 className="font-bold text-2xl mb-2">{p.title}</h4>
                <p className="text-mutedGray">{p.body}</p>
              </div>
            ))}
          </div>

          {/* Case studies / testimonials */}
          {/* <div className="space-y-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-brandLightNavy/5 rounded-xl p-6 flex flex-col md:flex-row items-start gap-6"
              >
                <img
                  src={founder}
                  alt={t.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
                <div>
                  <div className="font-bold text-brandNavy">{t.name}</div>
                  <div className="text-sm text-mutedGray mb-2">{t.role}</div>
                  <p className="text-mutedGray">{t.text}</p>
                </div>
              </div>
            ))}
          </div> */}
        </div>
      </section>

      {/* O = OFFER */}
      <section className="py-16 px-6 bg-gradient-to-br from-brandNavy to-brandLightNavy text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            Introducing the D.B.C. Formula (Full Course)
          </h3>

          <p className="text-2xl text-white/90 mb-6">
            When you combine discovery (what to sell), authority (how to build
            an audience), and conversion (how to sell predictably), you remove
            guesswork and create income that scales. The D.B.C. Formula is the
            step-by-step system that does exactly that.
          </p>

          {/* Course Module Overview */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-6 text-left">
            <h4 className="text-xl font-bold mb-4 text-center">
              What You'll Learn
            </h4>
            <div className="space-y-4">
              {modules.map((module, i) => (
                <div
                  key={i}
                  className="border-b border-white/20 pb-4 last:border-0"
                >
                  <div className="flex items-start gap-3">
                    <div className="bg-brandRed text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                      {module.number}
                    </div>
                    <div>
                      <h5 className="font-bold text-lg mb-1">{module.title}</h5>
                      <p className="text-white/80 text-sm">{module.subtitle}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white text-brandNavy rounded-2xl p-6 mb-6">
            <div className="text-sm text-mutedGray mb-2">Course Length</div>
            <div className="text-2xl font-bold mb-2">4 weeks</div>
            <div className="text-sm text-mutedGray mb-4">
              + 3 months ongoing support (bi-weekly coaching calls)
            </div>

            <div className="text-sm text-mutedGray">Price</div>
            <div className="text-4xl font-bold text-brandRed mb-2">₦9,999</div>
            <div className="text-sm text-mutedGray">
              Split into 2 payments available
            </div>
          </div>

          <p className="text-white/90 mb-6">
            The course guides you from idea to first sale, then to an automated
            system that scales. You'll get templates, scripts, and live coaching
            — plus community support so you don't have to figure this out alone.
          </p>

          <Link
            to="/dbc/buy"
            className="inline-block bg-white text-brandNavy px-8 py-3 rounded-2xl font-bold text-2xl hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
          >
            Enrol Now — ₦9,999 →
          </Link>
        </div>
      </section>

      {/* R = RESPONSE - Clear CTA and next steps */}
      <section id="enrol" className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to turn one skill into a reliable income stream?
          </h3>

          <p className="text-2xl text-mutedGray mb-6">
            Reserve your spot for the next cohort. Limited seats per cohort to
            preserve personalized feedback — only {spotsRemaining} spots left.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 text-left">
              <h4 className="font-bold mb-2">What's included</h4>
              <ul className="space-y-2 text-mutedGray">
                <li className="flex items-start gap-2">
                  <CheckIcon className="text-brandRed mt-1" />
                  <span>4-week intensive course (video + worksheets)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="text-brandRed mt-1" />
                  <span>3 months support & bi-weekly coaching calls</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="text-brandRed mt-1" />
                  <span>WhatsApp community and accountability checks</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="text-brandRed mt-1" />
                  <span>
                    Templates for sales pages, email sequences & offers
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-green-50 border-2 border-green-500 rounded-xl p-6 text-left">
              <h4 className="font-bold mb-2">Money-back guarantee</h4>
              <p className="text-mutedGray">
                Follow the system and implement the agreed actions. If you don't
                see the promised clarity or first traction within 30 days, we'll
                refund your investment.
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-center gap-4">
            <Link
              to="/dbc/buy"
              className="inline-block bg-brandRed text-white px-8 py-3 rounded-2xl font-bold text-2xl hover:bg-red-700 transition-all shadow-xl"
            >
              Enrol Now — ₦9,999
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brandNavy text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-white/70 text-sm">
            © {new Date().getFullYear()} D.B.C. Formula. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="text-white/70 hover:text-white">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/70 hover:text-white">
              Terms of Service
            </Link>
            <Link to="/contact" className="text-white/70 hover:text-white">
              Contact Us
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DBCLandingPage;
